// Module 10 — the plant-column applier: turns SceneSpec.plants (10.6 encoder output) into real Cesium
// geometry. Each generator draws as a vertical CylinderGeometry tower at its TRUE coord — height is
// log-scaled by capMw (a DISPLAY transform the encoder already applied; never the solver), fuel hue is
// the plant's identity, output intensity (fillFrac) modulates opacity. Geometry is STATIC (coord +
// capMw-derived height never change), so per-tick dispatch only mutates the per-instance COLOUR in
// place via getGeometryInstanceAttributes — no geometry rebuild, no FPS cliff (same discipline as the
// walls applier). Boundary: same-module + domain only.
import type { PlantSpec } from "./effects/spec";
import type { CesiumLike, SceneLike, PrimitiveLike, GeometryInstanceLike, ColorLike } from "./cesium-api";

const TOP_RADIUS_M = 110; // slight taper: narrower at the top so the tower reads as a 3D column
const BOTTOM_RADIUS_M = 190;

export interface PlantStyle {
  hex: string;
  alpha: number;
}

// Fuel hue is the plant's identity; output intensity modulates opacity (harder-working ⇒ brighter/more
// opaque). An unlit plant (outputMw 0) reads as a faint dark-slate stub — present but idle — so a
// powered-down unit is distinct from a producing one by BOTH hue and alpha, never colour alone.
export function plantStyle(p: PlantSpec): PlantStyle {
  // Batch C 4.10: a retired/decommissioned plant (Nanticoke, Lambton, Lakeview, Pickering-A23) reads as
  // a VERY faint grey stub — distinct from an idle-but-operating unit (#37474f a=0.25) and a lit one.
  if (p.status === "Retired" || p.status === "Decommissioned") return { hex: "#909090", alpha: 0.15 };
  if (!p.lit) return { hex: "#37474f", alpha: 0.25 };
  const f = Math.max(0, Math.min(1, p.fillFrac));
  return { hex: p.color, alpha: 0.45 + 0.5 * f };
}

export interface PlantLayer {
  apply(plants: readonly PlantSpec[]): void;
  setShow(visible: boolean): void; // perf-diagnosis lever (primitive.show — no rebuild)
  setModelMatrix(m: unknown): void; // 3D "float" lift — Matrix4 composed over the per-instance placement
}

export function makePlantLayer(cesium: CesiumLike, scene: SceneLike): PlantLayer {
  let primitive: PrimitiveLike | null = null;
  let liftMatrix: unknown = null; // pending 3D lift if set before the primitive is built
  // identity-stable instance ids: pickable {kind:"node", id:busId} objects, reused for the
  // in-place recolour lookups (Cesium matches getGeometryInstanceAttributes ids by identity)
  const idOf = new Map<string, { kind: "node"; id: string }>();
  const colorOf = (p: PlantSpec): ColorLike => {
    const s = plantStyle(p);
    return cesium.Color.fromCssColorString(s.hex).withAlpha(s.alpha);
  };

  return {
    apply(plants): void {
      if (plants.length === 0) return; // no generators (v1-shaped model) → no columns (not an error)
      if (!primitive) {
        const instances: GeometryInstanceLike[] = plants.map((p) => {
          const geometry = new cesium.CylinderGeometry({
            length: p.heightM, topRadius: TOP_RADIUS_M, bottomRadius: BOTTOM_RADIUS_M,
          });
          // centred cylinder placed with its midpoint at heightM/2 → it spans 0..heightM above ground.
          const origin = cesium.Cartesian3.fromDegrees(p.coord.lon, p.coord.lat, p.heightM / 2);
          const pickId = { kind: "node" as const, id: p.busId };
          idOf.set(p.id, pickId);
          return new cesium.GeometryInstance({
            geometry,
            modelMatrix: cesium.Transforms.eastNorthUpToFixedFrame(origin),
            id: pickId, // pickable: clicking a tower selects its BUS (inspect + Turn off both work)
            attributes: { color: cesium.ColorGeometryInstanceAttribute.fromColor(colorOf(p)) },
          });
        });
        primitive = new cesium.Primitive({
          geometryInstances: instances,
          appearance: new cesium.PerInstanceColorAppearance({ flat: false, translucent: true }), // shaded 3D column
          asynchronous: true, // build off-thread (default); never block the main thread on geometry compile (see walls.ts)
        });
        if (liftMatrix != null) primitive.modelMatrix = liftMatrix; // apply a lift set before build
        scene.primitives.add(primitive);
        return;
      }
      // already built — recolour in place from the new per-tick dispatch (no geometry rebuild).
      // Skip until the primitive has rendered once (Cesium refuses getGeometryInstanceAttributes before
      // its first update pass; a hidden tab pauses rAF while snapshots keep arriving) — same guard as walls.
      if (!primitive.ready) return;
      for (const p of plants) {
        const pickId = idOf.get(p.id);
        if (!pickId) continue;
        const attr = primitive.getGeometryInstanceAttributes(pickId); // same identity as at build
        attr.color = cesium.ColorGeometryInstanceAttribute.toValue(colorOf(p), attr.color); // real Cesium API: toValue(color, result)
      }
    },
    setShow(visible): void {
      if (primitive) primitive.show = visible;
    },
    setModelMatrix(m): void {
      liftMatrix = m;
      if (primitive) primitive.modelMatrix = m;
    },
  };
}
